/**
 * The same list, grouped by status.
 *
 * Used by all four list screens. Each supplies its own columns — pipeline
 * stage for leads, event status, post status, ambassador status — and its own
 * cards; nothing here knows what a lead or an event is.
 *
 * Clicking a card opens the same drawer a table row does. That is the point of
 * the view toggle: it changes how the same records are arranged, not what
 * happens to them.
 *
 * Empty columns are kept rather than hidden. A pipeline with nothing in
 * "Proposal" is telling you something, and a board that quietly drops the
 * column makes that invisible.
 */
import { useRef, useState } from 'react'
import './KanbanBoard.css'

export interface KanbanColumn {
  key: string
  label: string
  /** The status colour; the design tints only the heading, not the column. */
  color?: string
  /**
   * A sentence under the heading, saying what the column is for.
   *
   * Only the task backlog uses it — decision 58 asks the silo to state its own
   * rule, because a column nothing flows *into* is not something a board makes
   * obvious on its own.
   */
  note?: string
  /**
   * Draw it as a silo: separated from the flow to its right.
   *
   * The board reads left to right as progress. A column that is a pool rather
   * than a stage has to look unlike its neighbours or it reads as the first
   * step of the same sequence.
   */
  silo?: boolean
}

export interface KanbanCard {
  id: string
  /** Which column this card belongs in. */
  group: string
  /** Which lane, on a board that has them — see `KanbanLane`. */
  lane?: string
  title: string
  /** One line under the title — region, date, whatever identifies it. */
  meta: string
  /** Small pill in the card footer: a score, a tier, a count. */
  badge?: { text: string; color?: string; bg?: string } | null
  /** Right of the badge — usually the owner. */
  trailing?: string
}

/**
 * A row on a board that distributes work as well as tracking it — decision 102.
 *
 * The Sales CRM board answers two questions at once: what stage is this lead at,
 * and whose is it. Columns carry the first and lanes the second, so moving a
 * card sideways advances it and moving it down hands it over — and one drop can
 * legally do both.
 *
 * Boards without lanes are untouched: Ownership and Transfers pass none and
 * render exactly as before. The card recipe is shared on purpose, because two
 * card designs for one record is how they drift.
 */
export interface KanbanLane {
  key: string
  label: string
  /**
   * The pool being distributed, tinted apart and kept first.
   *
   * Unassigned is not one assignee among many — it is the work nobody has yet,
   * which is the same argument decision 58 makes for the task backlog being a
   * silo rather than a column.
   */
  pool?: boolean
}

/**
 * One action offered on the cards of one column.
 *
 * Cards open a record everywhere in this app, so a second thing to press has to
 * be worth the ambiguity. The backlog is the case that earns it: pulling work
 * into To do is the *only* thing that happens to a backlog card, and making
 * somebody open the record to change a field to do it is three clicks for a
 * decision they already made when they looked at the column.
 */
export interface KanbanCardAction {
  /** Which column's cards carry it. */
  column: string
  label: string
  onAct: (id: string) => void
}

interface Props {
  columns: KanbanColumn[]
  cards: KanbanCard[]
  selectedId?: string | null
  onSelect: (id: string) => void
  emptyNote?: string
  cardAction?: KanbanCardAction
  /** Rows. Absent means the plain one-row-of-columns board — decision 102. */
  lanes?: KanbanLane[]
  /**
   * Where a card was dropped. Only called when something actually changed.
   *
   * Both halves come through together because one drop may change both, and
   * the caller needs to log them as the two facts they are rather than guess
   * which moved.
   */
  onMove?: (id: string, to: { column: string; lane: string }) => void
}

export function KanbanBoard({
  columns,
  cards,
  selectedId = null,
  onSelect,
  emptyNote,
  cardAction,
  lanes,
  onMove,
}: Props) {
  /**
   * The card in the air, and whether a drag just happened.
   *
   * `dragged` is the payload; `justDragged` is what keeps decision 102's "a drag
   * is not a click" true. A card is a button so that it opens the record on
   * click and works from the keyboard, and some browsers fire that click at the
   * end of a drag — which would open the drawer over the board every time
   * somebody reassigned a lead.
   */
  const [dragged, setDragged] = useState<{ id: string; column: string; lane: string } | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const justDragged = useRef(false)
  /**
   * One card, used by both layouts — decision 102 asks for the shared recipe.
   *
   * A button, so a click opens the record and the keyboard reaches it. Draggable
   * only where the board can act on a drop: on a board without lanes there is
   * nowhere to drop that means anything, and a card that lifts and does nothing
   * teaches people the board is broken.
   */
  const renderCard = (c: KanbanCard) => {
    const canDrag = !!onMove && !!lanes
    return (
      <button
        key={c.id}
        className={
          `kanban__card${c.id === selectedId ? ' kanban__card--selected' : ''}` +
          (dragged?.id === c.id ? ' kanban__card--dragging' : '')
        }
        draggable={canDrag}
        onDragStart={(e) => {
          if (!canDrag) return
          justDragged.current = false
          setDragged({ id: c.id, column: c.group, lane: c.lane ?? '' })
          // Firefox refuses to start a drag without data on the transfer.
          e.dataTransfer.setData('text/plain', c.id)
          e.dataTransfer.effectAllowed = 'move'
        }}
        onDragEnd={() => {
          setDragged(null)
          setOver(null)
        }}
        onClick={(e) => {
          // A drag is not a click — see `justDragged`.
          if (justDragged.current) {
            justDragged.current = false
            e.preventDefault()
            return
          }
          onSelect(c.id)
        }}
      >
                  <span className="kanban__title" title={c.title}>
                    {c.title}
                  </span>
                  <span className="kanban__meta" title={c.meta}>
                    {c.meta}
                  </span>
                  <span className="kanban__foot">
                    {c.badge ? (
                      <span
                        className="kanban__badge"
                        style={{ background: c.badge.bg, color: c.badge.color }}
                      >
                        {c.badge.text}
                      </span>
                    ) : (
                      <span />
                    )}
                    {c.trailing && <span className="kanban__trailing">{c.trailing}</span>}
                  </span>
                  {cardAction?.column === c.group && (
                    // A span, not a button: this sits inside the card's own
                    // button and nesting one inside another is invalid HTML that
                    // browsers resolve by breaking the outer one.
                    <span
                      role="button"
                      tabIndex={0}
                      className="kanban__action"
                      onClick={(e) => {
                        e.stopPropagation()
                        cardAction.onAct(c.id)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          cardAction.onAct(c.id)
                        }
                      }}
                    >
                      {cardAction.label}
                    </span>
                  )}
                </button>
    )
  }

  if (cards.length === 0 && emptyNote) {
    return <div className="kanban__empty">{emptyNote}</div>
  }

  /**
   * The distribution board — decision 102.
   *
   * Columns are stages and lanes are people, so the two things a drop can mean
   * are the two axes: sideways advances the lead, down hands it over, and a
   * diagonal does both in one gesture, which the decision explicitly allows.
   *
   * **Every cell exists, including the empty ones.** An empty lane-cell is where
   * a lead goes when you give it to somebody who has none at that stage — if it
   * collapsed there would be nothing to drop on, and the board could only ever
   * move work to people who already had some.
   */
  if (lanes && lanes.length > 0) {
    const grid = { gridTemplateColumns: `var(--lane-w) repeat(${columns.length}, minmax(210px, 1fr))` }
    const cellKey = (lane: string, column: string) => `${lane} ${column}`

    const drop = (lane: string, column: string) => (e: React.DragEvent) => {
      e.preventDefault()
      setOver(null)
      const d = dragged
      setDragged(null)
      if (!d || !onMove) return
      // Nothing changed: not a move, and not worth a log entry either.
      if (d.lane === lane && d.column === column) return
      justDragged.current = true
      onMove(d.id, { column, lane })
    }

    return (
      <div className="laneboard" style={{ ['--lane-w' as string]: '150px' }}>
        <div className="laneboard__grid" style={grid}>
          {/* The corner is empty on purpose: the lane column has no heading
              because the lanes label themselves down the side. */}
          <div className="laneboard__corner" />
          {columns.map((col) => (
            <div className="laneboard__colhead" key={col.key}>
              <span className="laneboard__collabel" style={{ color: col.color }}>
                {col.label}
              </span>
              <span className="kanban__colcount">
                {cards.filter((c) => c.group === col.key).length}
              </span>
            </div>
          ))}

          {lanes.map((lane) => {
            const mine = cards.filter((c) => (c.lane ?? '') === lane.key)
            return (
              // No template here — the row is `display: contents`, so these
              // cells are laid out by the grid above. See the CSS.
              <div className="laneboard__row" key={lane.key}>
                <div
                  className={`laneboard__lanehead${lane.pool ? ' laneboard__lanehead--pool' : ''}`}
                >
                  <span className="laneboard__lanelabel" title={lane.label}>
                    {lane.label}
                  </span>
                  <span className="kanban__colcount">{mine.length}</span>
                </div>
                {columns.map((col) => {
                  const items = mine.filter((c) => c.group === col.key)
                  const key = cellKey(lane.key, col.key)
                  const isTarget =
                    !!dragged && (dragged.lane !== lane.key || dragged.column !== col.key)
                  return (
                    <div
                      key={key}
                      className={
                        'laneboard__cell' +
                        (lane.pool ? ' laneboard__cell--pool' : '') +
                        (over === key ? ' laneboard__cell--over' : '') +
                        (isTarget ? ' laneboard__cell--target' : '')
                      }
                      onDragOver={(e) => {
                        if (!dragged || !onMove) return
                        // Without this the browser refuses the drop entirely.
                        e.preventDefault()
                        e.dataTransfer.dropEffect = 'move'
                        if (over !== key) setOver(key)
                      }}
                      onDragLeave={() => setOver((cur) => (cur === key ? null : cur))}
                      onDrop={drop(lane.key, col.key)}
                    >
                      {items.map((c) => renderCard(c))}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="kanban">
      {columns.map((col) => {
        const items = cards.filter((c) => c.group === col.key)
        return (
          <section
            className={`kanban__col${col.silo ? ' kanban__col--silo' : ''}`}
            key={col.key}
            aria-label={col.label}
          >
            <header className="kanban__colhead">
              <span className="kanban__collabel" style={{ color: col.color }}>
                {col.label}
              </span>
              <span className="kanban__colcount">{items.length}</span>
            </header>
            {col.note && <p className="kanban__colnote">{col.note}</p>}

            <div className="kanban__cards">
              {items.map((c) => renderCard(c))}
            </div>
          </section>
        )
      })}
    </div>
  )
}
